import { ref, uploadBytes, getDownloadURL, deleteObject, listAll } from "firebase/storage";
import { setDoc, deleteDoc, doc, collection, getDocs, query, where } from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { drawOnCanvas } from "./canvasUtils";
import { db } from '../firebaseConfig';


export const handleDownloadClick = async (canvasRef, gridSize, currentUser, storage, matrixData, layoutName, logoSrc, actualDimensions) => {
    // Handle case where we're downloading from an AI layout (no canvasRef)
    if (matrixData) {
        // Create a temporary canvas to render the AI layout
        const tempCanvas = document.createElement('canvas');
        
        // Parse grid dimensions
        const [cols, rows] = gridSize.split('x').map(Number);
        
        // Use actual dimensions if provided, otherwise fall back to analysis or defaults
        let tileDimensions = actualDimensions;
        
        if (!tileDimensions) {
            // Try to get from localStorage analysis
            try {
                const analyzedDimStr = localStorage.getItem('analyzedTileDimensions');
                if (analyzedDimStr) {
                    const analyzed = JSON.parse(analyzedDimStr);
                    if (analyzed && analyzed.tileWidthPixels && analyzed.tileHeightPixels) {
                        tileDimensions = {
                            tileWidthPixels: analyzed.tileWidthPixels,
                            tileHeightPixels: analyzed.tileHeightPixels,
                            rows: analyzed.rows,
                            columns: analyzed.columns
                        };
                    }
                }
            } catch (error) {
                console.error("Error reading analyzed dimensions:", error);
            }
        }

        if (!tileDimensions) {
            // Try to get from sessionStorage
            try {
                const sessionDimensions = sessionStorage.getItem('quiltDimensions');
                if (sessionDimensions) {
                    const dims = JSON.parse(sessionDimensions);
                    if (dims && dims.tileWidthPixels && dims.tileHeightPixels) {
                        tileDimensions = {
                            tileWidthPixels: dims.tileWidthPixels,
                            tileHeightPixels: dims.tileHeightPixels,
                            rows: dims.rows,
                            columns: dims.columns
                        };
                    }
                }
            } catch (error) {
                console.error("Error reading session dimensions:", error);
            }
        }
        
        // Use tile dimensions or defaults
        const tileWidth = tileDimensions?.tileWidthPixels || 300;
        const tileHeight = tileDimensions?.tileHeightPixels || 300;
        
        console.log("Using tile dimensions for download:", {
            tileWidth,
            tileHeight,
            cols,
            rows,
            source: actualDimensions ? "provided" : tileDimensions ? "analyzed/session" : "default"
        });
        
        const margin = 20;
        const watermarkHeight = 50; // Dedicated height for watermark
        const totalWidth = cols * tileWidth + margin * 2;
        const totalHeight = rows * tileHeight + margin * 2 + watermarkHeight;
        
        // Ensure canvas is large enough for the entire grid
        tempCanvas.width = totalWidth;
        tempCanvas.height = totalHeight;
        
        // Get canvas context and set high quality rendering
        const ctx = tempCanvas.getContext('2d', { alpha: false });
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Fill with white background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
        
        console.log(`Preparing download canvas for ${cols}x${rows} grid with tile size ${tileWidth}x${tileHeight}px`);
        
        // Create a position map for the quilt tiles from window.quiltGridData
        let tileMap = {};
        
        // First try to get the quilt grid data from window object (most reliable source)
        if (window.quiltGridData && Array.isArray(window.quiltGridData)) {
            console.log(`Found ${window.quiltGridData.length} tiles in window.quiltGridData`);
            window.quiltGridData.forEach(tile => {
                if (tile && tile.position) {
                    const [row, col] = tile.position;
                    const key = `${row},${col}`;
                    tileMap[key] = tile;
                }
            });
        }
        // Fall back to sessionStorage if window object doesn't have the data
        else {
            try {
                const quiltGridData = JSON.parse(sessionStorage.getItem('quiltGridData'));
                if (quiltGridData && Array.isArray(quiltGridData)) {
                    console.log(`Found ${quiltGridData.length} tiles in sessionStorage`);
                    quiltGridData.forEach(tile => {
                        if (tile && tile.position) {
                            const [row, col] = tile.position;
                            const key = `${row},${col}`;
                            tileMap[key] = tile;
                        }
                    });
                }
            } catch (error) {
                console.error("Error accessing tile map from sessionStorage:", error);
            }
        }
        
        // Last resort: look for quilt data in DOM elements
        if (Object.keys(tileMap).length === 0) {
            // Try to find all canvas elements that might contain our tiles
            const canvasElements = document.querySelectorAll('canvas');
            canvasElements.forEach(canvas => {
                try {
                    // Skip the canvas we're creating
                    if (canvas !== tempCanvas) {
                        // Extract the quiltGrid data from the canvas's associated data
                        const canvasData = canvas.__quiltData || canvas.dataset.quiltGrid;
                        if (canvasData) {
                            const parsedData = typeof canvasData === 'string' ? 
                                JSON.parse(canvasData) : canvasData;
                            
                            if (Array.isArray(parsedData)) {
                                parsedData.forEach(tile => {
                                    if (tile && tile.position) {
                                        const [row, col] = tile.position;
                                        const key = `${row},${col}`;
                                        tileMap[key] = tile;
                                    }
                                });
                            }
                        }
                    }
                } catch (e) {
                    console.error("Error extracting tile data from canvas:", e);
                }
            });
        }
        
        // Log how many tiles we found for debugging
        console.log(`Found ${Object.keys(tileMap).length} total tiles in tile map`);
        
        // Create tile objects array from matrix data for drawing
        const tiles = [];
        const missingPositions = [];
        
        if (Array.isArray(matrixData)) {
            matrixData.forEach((row, rowIndex) => {
                if (Array.isArray(row)) {
                    row.forEach((position, colIndex) => {
                        // Position could be either a string like "0,1" or an array [0,1]
                        let originalRow, originalCol;
                        let posKey;
                        
                        if (typeof position === 'string') {
                            [originalRow, originalCol] = position.split(',').map(Number);
                            posKey = position;
                        } else if (Array.isArray(position) && position.length === 2) {
                            [originalRow, originalCol] = position;
                            posKey = `${originalRow},${originalCol}`;
                        } else {
                            console.error("Invalid position format:", position);
                            missingPositions.push(`Invalid at ${rowIndex},${colIndex}`);
                            return;
                        }
                        
                        const originalTile = tileMap[posKey];
                        if (originalTile) {
                            tiles.push({
                                ...originalTile,
                                position: [rowIndex, colIndex],
                                originalPosition: [originalRow, originalCol]
                            });
                        } else {
                            missingPositions.push(posKey);
                        }
                    });
                }
            });
        }
        
        // Log missing positions for debugging
        if (missingPositions.length > 0) {
            console.warn(`Missing ${missingPositions.length} tile positions:`, missingPositions);
        }
        
        // Fall back to original positions if we're missing too many tiles
        if (tiles.length < rows * cols / 2) {
            console.warn(`Too few tiles found (${tiles.length}/${rows*cols}), attempting fallback...`);
            
            // Create a new tiles array using matrixData directly
            tiles.length = 0;
            if (Array.isArray(matrixData)) {
                // Extract all available values from tileMap for fallback use
                const availableTiles = Object.values(tileMap);
                
                matrixData.forEach((row, rowIndex) => {
                    if (Array.isArray(row)) {
                        row.forEach((position, colIndex) => {
                            // Try to find an appropriate tile to use
                            let tileToUse = null;
                            
                            // First try using the position data
                            if (typeof position === 'string' || Array.isArray(position)) {
                                const posKey = typeof position === 'string' ? 
                                    position : `${position[0]},${position[1]}`;
                                tileToUse = tileMap[posKey];
                            }
                            
                            // If we couldn't find a specific tile, use any available tile
                            if (!tileToUse && availableTiles.length > 0) {
                                // Use a deterministic index based on position
                                const index = (rowIndex * cols + colIndex) % availableTiles.length;
                                tileToUse = availableTiles[index];
                            }
                            
                            // If we found a tile to use, add it to our tiles array
                            if (tileToUse) {
                                tiles.push({
                                    ...tileToUse,
                                    position: [rowIndex, colIndex]
                                });
                            }
                        });
                    }
                });
            }
            
            console.log(`Fallback: Created ${tiles.length} tiles for rendering`);
        }
        
        // If we found tiles, try to render them
        if (tiles.length > 0) {
            try {
                // Debug info
                console.log(`Rendering ${tiles.length} tiles for download (expected ${rows * cols})`);
                
                // Draw tiles with actual tile dimensions
                const loadImages = tiles.map(tile => {
                    return new Promise((resolve) => {
                        if (tile.url) {
                            const img = new Image();
                            img.crossOrigin = "anonymous";
                            img.onload = () => {
                                // Calculate position based on tile's position in the matrix
                                const x = margin + tile.position[1] * tileWidth;
                                const y = margin + tile.position[0] * tileHeight;
                                
                                try {
                                    ctx.drawImage(img, x, y, tileWidth, tileHeight);
                                    
                                    // Draw border
                                    ctx.strokeStyle = '#ddd';
                                    ctx.lineWidth = 1;
                                    ctx.strokeRect(x, y, tileWidth, tileHeight);
                                } catch (drawError) {
                                    console.error("Error drawing tile:", drawError);
                                }
                                resolve();
                            };
                            img.onerror = () => {
                                // Draw placeholder for failed loads
                                const x = margin + tile.position[1] * tileWidth;
                                const y = margin + tile.position[0] * tileHeight;
                                
                                ctx.fillStyle = '#f5f5f5';
                                ctx.fillRect(x, y, tileWidth, tileHeight);
                                
                                ctx.strokeStyle = '#ddd';
                                ctx.strokeRect(x, y, tileWidth, tileHeight);
                                
                                resolve();
                            };
                            img.src = tile.url;
                        } else {
                            resolve();
                        }
                    });
                });
                
                // Wait for all images to load, then add logo and watermark before downloading
                Promise.all(loadImages).then(() => {
                    // Draw a separator line between tiles and watermark area
                    ctx.strokeStyle = '#eeeeee';
                    ctx.lineWidth = 1;
                    ctx.beginPath();
                    ctx.moveTo(0, tempCanvas.height - watermarkHeight);
                    ctx.lineTo(tempCanvas.width, tempCanvas.height - watermarkHeight);
                    ctx.stroke();
                    
                    // Create a clean white background for the watermark area
                    ctx.fillStyle = 'white';
                    ctx.fillRect(0, tempCanvas.height - watermarkHeight, tempCanvas.width, watermarkHeight);
                    
                    // Add logo if provided
                    if (logoSrc) {
                        const logoImg = new Image();
                        logoImg.crossOrigin = "anonymous";
                        logoImg.onload = () => {
                            // Draw logo at bottom right
                            const logoSize = 18;
                            const logoX = tempCanvas.width - logoSize - 15;
                            const logoY = tempCanvas.height - logoSize - 10;
                            
                            // Draw logo
                            ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
                            
                            // Add watermark text next to logo
                            ctx.font = 'bold 12px Arial';
                            ctx.fillStyle = 'black';
                            ctx.textAlign = 'right';
                            ctx.fillText('Generated with NOSTAI', logoX - 5, tempCanvas.height - 15);
                            
                            // Create and trigger download
                            createAndDownloadImage(tempCanvas, gridSize, layoutName);
                        };
                        logoImg.onerror = () => {
                            console.error("Failed to load logo for download");
                            // Still add watermark text if logo fails
                            ctx.font = 'bold 12px Arial';
                            ctx.fillStyle = 'black';
                            ctx.textAlign = 'right';
                            ctx.fillText('Generated with NOSTAI', tempCanvas.width - 20, tempCanvas.height - 15);
                            
                            // Create and trigger download even if logo fails
                            createAndDownloadImage(tempCanvas, gridSize, layoutName);
                        };
                        logoImg.src = logoSrc;
                    } else {
                        // Just add watermark text if no logo
                        ctx.font = 'bold 12px Arial';
                        ctx.fillStyle = 'black';
                        ctx.textAlign = 'right';
                        ctx.fillText('Generated with NOSTAI', tempCanvas.width - 20, tempCanvas.height - 15);
                        
                        // Create and trigger download
                        createAndDownloadImage(tempCanvas, gridSize, layoutName);
                    }
                });
                
                // Helper function to create and trigger download
                function createAndDownloadImage(canvas, gridSize, layoutName) {
                    const currentDate = new Date();
                    const formattedDate = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1)
                        .toString()
                        .padStart(2, '0')}-${currentDate.getDate().toString().padStart(2, '0')}`;
                    const fileName = `Nostai_${layoutName || 'AILayout'}_${gridSize}_${formattedDate}.png`;
                    
                    // Export at high quality as PNG for lossless quality
                    const link = document.createElement("a");
                    link.href = canvas.toDataURL("image/png");
                    link.download = fileName;
                    link.click();
                }
            } catch (error) {
                console.error("Error rendering AI layout:", error);
                alert("Sorry, there was an error downloading this layout. Please try again.");
            }
        } else {
            console.error("No tiles found for rendering AI layout");
            alert("Could not download the layout due to missing tile data. Please try generating a new layout.");
        }
        
        return;
    }

    // Original code for regular layouts with canvas reference
    const canvas = canvasRef?.current;
    if (!canvas) {
        console.error("Canvas element not found.");
        return;
    }

    // Create a new canvas to add the watermark without modifying the original
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = canvas.width;
    outputCanvas.height = canvas.height + 40; // Add extra space at the bottom for watermark
    const ctx = outputCanvas.getContext('2d');
    
    // Fill the background with white
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, outputCanvas.width, outputCanvas.height);
    
    // Draw the original canvas content
    ctx.drawImage(canvas, 0, 0);
    
    // Create fixed watermark space at bottom - consistent with AI layout
    const watermarkAreaHeight = 30;
    
    // Create a clean white background for the watermark area
    ctx.fillStyle = 'white';
    ctx.fillRect(0, outputCanvas.height - watermarkAreaHeight, outputCanvas.width, watermarkAreaHeight);
    
    // Add separator line
    ctx.strokeStyle = '#eeeeee';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, outputCanvas.height - watermarkAreaHeight);
    ctx.lineTo(outputCanvas.width, outputCanvas.height - watermarkAreaHeight);
    ctx.stroke();
    
    // Add logo if provided
    if (logoSrc) {
        const logoImg = new Image();
        logoImg.crossOrigin = "anonymous";
        logoImg.onload = () => {
            // Draw logo at bottom right
            const logoSize = 25;
            const logoX = outputCanvas.width - logoSize - 15;
            const logoY = outputCanvas.height - logoSize - 2;
            
            // Draw logo
            ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
            
            // Add watermark text next to logo
            ctx.font = 'bold 12px Arial';
            ctx.fillStyle = 'black';
            ctx.textAlign = 'right';
            ctx.fillText('Generated with NOSTAI', logoX - 5, outputCanvas.height - 15);
            
            // Continue with download - remove the content of this function and directly call finishDownload
            finishDownload();
        };
        logoImg.onerror = () => {
            console.error("Failed to load logo for download");
            // Still add watermark text if logo fails
            ctx.font = 'bold 12px Arial';
            ctx.fillStyle = 'black';
            ctx.textAlign = 'right';
            ctx.fillText('Generated with NOSTAI', outputCanvas.width - 20, outputCanvas.height - 15);
            
            // Continue with download even if logo fails
            finishDownload();
        };
        logoImg.src = logoSrc;
    } else {
    
        finishDownload();
    }
    
    // Helper function to finish the download process
    async function finishDownload() {
        const imageUrl = outputCanvas.toDataURL("image/jpeg");
        const blob = await (await fetch(imageUrl)).blob();

        const currentDate = new Date();
        const formattedDate = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1)
            .toString()
            .padStart(2, '0')}-${currentDate.getDate().toString().padStart(2, '0')}`;
        const fileName = `Nostai_QuiltLayout_${gridSize}_${formattedDate}.jpg`;

        if (currentUser && currentUser.uid) {
            const storageRef = ref(storage, `user_quilts/${currentUser.uid}/${fileName}`);
            try {
                await uploadBytes(storageRef, blob);
                console.log("Quilt saved to storage!");
            } catch (error) {
                console.error("Error uploading quilt:", error);
            }
        } else {
            console.error("User not authenticated. Unable to save quilt.");
            return;
        }

        const link = document.createElement("a");
        link.href = imageUrl;
        link.download = fileName;
        link.click();
    }
};



export const handleShareClick = async (
    canvasRef,
    setShowModal,
    setShareLink,
    currentUser,
    storage,
    db
) => {
    setShowModal(true);
    setShareLink("");

    const canvas = canvasRef.current;
    if (!canvas) {
        console.error("Canvas not found");
        setShowModal(false);
        return;
    }

    try {
        const imageUrl = canvas.toDataURL("image/jpeg");
        const blob = await (await fetch(imageUrl)).blob();

        const designId = `${currentUser.uid}-${uuidv4()}`;
        const storageRef = ref(storage, `user_quilts/${currentUser.uid}/${designId}.jpg`);
        await uploadBytes(storageRef, blob);
        const publicUrl = await getDownloadURL(storageRef);

        await setDoc(doc(db, "shared_quilts", designId), {
            quiltImageUrl: publicUrl,
            userId: currentUser.uid,
            createdAt: new Date(),
        });

        const shareableLink = `https://nostai.com/c/${designId}`;
        setShareLink(shareableLink);
    } catch (error) {
        console.error("Error sharing quilt:", error);
        setShareLink("Error creating link");
    }
};



export const handleShareAIClick = async (
    layout,
    layoutName,
    setShowModal,
    setShareLink,
    currentUser,
    storage,
    db,
    quiltGrid,
    gridSize,
    logoSrc
) => {
    setShowModal(true);
    setShareLink("");

    // Create a position map for the original quilt grid
    const tilePositionMap = {};
    quiltGrid.forEach(tile => {
        if (tile && tile.position) {
            const [row, col] = tile.position;
            tilePositionMap[`${row},${col}`] = tile;
        }
    });
    
    // Create a temporary canvas
    const tempCanvas = document.createElement("canvas");
    
    try {
        // Use the matrix from the layout to create a new arrangement
        const [columns, rows] = gridSize.split("x").map(Number);
        const layoutTiles = [];
        
        if (layout.matrix) {
            // Process each row in the matrix
            layout.matrix.forEach((row, rowIndex) => {
                // Process each position string in the row
                row.forEach((posStr, colIndex) => {
                    // Look up the tile in the original grid
                    const originalTile = tilePositionMap[posStr];
                    
                    if (originalTile) {
                        // Add the tile with its new position
                        layoutTiles.push({
                            ...originalTile,
                            position: [rowIndex, colIndex]
                        });
                    }
                });
            });
        }
        
        // Draw the canvas with the arranged tiles
        drawOnCanvas({ current: tempCanvas }, layoutTiles, gridSize, logoSrc);
        
        // Continue with uploading the image
        setTimeout(async () => {
            try {
                const imageUrl = tempCanvas.toDataURL("image/jpeg");
                const blob = await (await fetch(imageUrl)).blob();

                const designId = `${currentUser.uid}-${Date.now()}`;
                const storageRef = ref(storage, `user_quilts/${currentUser.uid}/${designId}.jpg`);
                await uploadBytes(storageRef, blob);
                const publicUrl = await getDownloadURL(storageRef);

                await setDoc(doc(db, "shared_quilts", designId), {
                    quiltImageUrl: publicUrl,
                    userId: currentUser.uid,
                    createdAt: new Date(),
                    layoutName: layoutName
                });

                const shareableLink = `https://nostai.com/c/${designId}`;
                setShareLink(shareableLink);
            } catch (error) {
                console.error("Error sharing AI quilt:", error);
                setShareLink("Error creating link");
            }
        }, 500);
    } catch (error) {
        console.error("Error preparing AI quilt for sharing:", error);
        setShareLink("Error preparing quilt image");
    }
};

//reset
export const confirmReset = async (
    sessionId,
    storage,
    db,
    setUploadedImages,
    setGridSize,
    setGenerated,
    setRequiredImages,
    setShowWarning,
    setQuiltImageUrl,
    setPreviousLayoutType,
    setGenerateDisabled,
    setIsEditUnlocked,
    setQuiltGrid,
    setSessionId,
    setFeedbackSubmitted,
    setRating,
    setIsResetLoading,
    setAIGeneratedLayouts,
    closeResetModal,
    setQuiltDimensions, // New parameter
    setLayoutSuggestions, // Add this parameter
    setCurrentStep // Add this parameter
) => {
    setIsResetLoading(true);
    try {
        console.log("Starting reset process for session:", sessionId);
        
        // Delete payment documents - IMPORTANT: add this block before cleaning up files and data
        if (db && sessionId) {
            try {
                console.log("Attempting to delete payment documents for user:", sessionId);
                
                // Delete payment documents in the customers/{userId}/payments collection
                const paymentsCollectionRef = collection(db, `customers/${sessionId}/payments`);
                const paymentsSnapshot = await getDocs(paymentsCollectionRef);
                
                if (!paymentsSnapshot.empty) {
                    console.log(`Found ${paymentsSnapshot.docs.length} payment documents to delete`);
                    
                    // Delete each payment document
                    for (const document of paymentsSnapshot.docs) {
                        console.log(`Deleting payment document: ${document.id}`);
                        await deleteDoc(doc(db, `customers/${sessionId}/payments`, document.id));
                    }
                    
                    console.log("All payment documents deleted successfully");
                } else {
                    console.log("No payment documents found to delete");
                }
                
                // Also check for and delete checkout_sessions
                const checkoutSessionsRef = collection(db, `customers/${sessionId}/checkout_sessions`);
                const checkoutSnapshot = await getDocs(checkoutSessionsRef);
                
                if (!checkoutSnapshot.empty) {
                    console.log(`Found ${checkoutSnapshot.docs.length} checkout sessions to delete`);
                    
                    // Delete each checkout session
                    for (const document of checkoutSnapshot.docs) {
                        console.log(`Deleting checkout session: ${document.id}`);
                        await deleteDoc(doc(db, `customers/${sessionId}/checkout_sessions`, document.id));
                    }
                    
                    console.log("All checkout sessions deleted successfully");
                } else {
                    console.log("No checkout sessions found to delete");
                }
                
                // Delete user_layout documents for this user
                try {
                    console.log("Deleting user_layout documents for user:", sessionId);
                    const userLayoutsRef = collection(db, 'user_layouts');
                    const userLayoutQuery = query(userLayoutsRef, where("userId", "==", sessionId));
                    const userLayoutSnapshot = await getDocs(userLayoutQuery);
                    
                    if (!userLayoutSnapshot.empty) {
                        console.log(`Found ${userLayoutSnapshot.docs.length} user_layout documents to delete`);
                        
                        for (const document of userLayoutSnapshot.docs) {
                            console.log(`Deleting user_layout document: ${document.id}`);
                            await deleteDoc(doc(db, 'user_layouts', document.id));
                        }
                        
                        console.log("All user_layout documents deleted successfully");
                    } else {
                        console.log("No user_layout documents found to delete");
                    }
                } catch (error) {
                    console.error("Error deleting user_layout documents:", error);
                }
                
                // Delete layout_customization documents for this user
                try {
                    console.log("Deleting layout_customization documents for user:", sessionId);
                    const layoutCustomizationsRef = collection(db, 'layout_customizations');
                    const layoutCustomizationQuery = query(layoutCustomizationsRef, where("userId", "==", sessionId));
                    const layoutCustomizationSnapshot = await getDocs(layoutCustomizationQuery);
                    
                    if (!layoutCustomizationSnapshot.empty) {
                        console.log(`Found ${layoutCustomizationSnapshot.docs.length} layout_customization documents to delete`);
                        
                        for (const document of layoutCustomizationSnapshot.docs) {
                            console.log(`Deleting layout_customization document: ${document.id}`);
                            await deleteDoc(doc(db, 'layout_customizations', document.id));
                        }
                        
                        console.log("All layout_customization documents deleted successfully");
                    } else {
                        console.log("No layout_customization documents found to delete");
                    }
                } catch (error) {
                    console.error("Error deleting layout_customization documents:", error);
                }
                
                // NEW: Delete shared_quilts collection documents for this user
                try {
                    console.log("Deleting shared_quilts documents for user:", sessionId);
                    const sharedQuiltsRef = collection(db, 'shared_quilts');
                    const sharedQuiltsQuery = query(sharedQuiltsRef, where("userId", "==", sessionId));
                    const sharedQuiltsSnapshot = await getDocs(sharedQuiltsQuery);
                    
                    if (!sharedQuiltsSnapshot.empty) {
                        console.log(`Found ${sharedQuiltsSnapshot.docs.length} shared_quilts documents to delete`);
                        
                        for (const document of sharedQuiltsSnapshot.docs) {
                            console.log(`Deleting shared_quilts document: ${document.id}`);
                            await deleteDoc(doc(db, 'shared_quilts', document.id));
                        }
                        
                        console.log("All shared_quilts documents deleted successfully");
                    } else {
                        console.log("No shared_quilts documents found to delete");
                    }
                } catch (error) {
                    console.error("Error deleting shared_quilts documents:", error);
                }
            } catch (error) {
                console.error("Error deleting payment documents:", error);
            }
        }

        // Delete all files from Firebase Storage
        if (sessionId) {
            try {
                // Delete quilt_designs folder and its contents
                const imagesRef = ref(storage, `quilt_designs/${sessionId}`);
                try {
                    const listResult = await listAll(imagesRef);
                    console.log(`Found ${listResult.items.length} files in quilt_designs/${sessionId}`);
                    // Delete each file
                    await Promise.all(listResult.items.map(itemRef => deleteObject(itemRef)));
                    console.log(`Deleted all files in quilt_designs/${sessionId}`);
                } catch (error) {
                    console.error(`Error deleting files in quilt_designs/${sessionId}:`, error);
                }
                
                // NEW: Delete emboss folder inside quilt_designs
                const embossRef = ref(storage, `quilt_designs/${sessionId}/emboss`);
                try {
                    const embossListResult = await listAll(embossRef);
                    console.log(`Found ${embossListResult.items.length} files in quilt_designs/${sessionId}/emboss`);
                    // Delete each file in emboss folder
                    await Promise.all(embossListResult.items.map(itemRef => deleteObject(itemRef)));
                    console.log(`Deleted all files in quilt_designs/${sessionId}/emboss`);
                } catch (error) {
                    // This might fail if folder doesn't exist, which is fine
                    console.log(`Note: emboss folder may not exist or couldn't be accessed: ${error.message}`);
                }
                
                // NEW: Delete user_quilts folder
                const userQuiltsRef = ref(storage, `user_quilts/${sessionId}`);
                try {
                    const userQuiltsListResult = await listAll(userQuiltsRef);
                    console.log(`Found ${userQuiltsListResult.items.length} files in user_quilts/${sessionId}`);
                    // Delete each file in user_quilts folder
                    await Promise.all(userQuiltsListResult.items.map(itemRef => deleteObject(itemRef)));
                    console.log(`Deleted all files in user_quilts/${sessionId}`);
                } catch (error) {
                    // This might fail if folder doesn't exist, which is fine
                    console.log(`Note: user_quilts folder may not exist or couldn't be accessed: ${error.message}`);
                }
                
                // Clear Firestore data with explicit null values for dimensions
                const sessionRef = doc(db, 'sessions', sessionId);
                await setDoc(sessionRef, {
                    uploadedImages: [],
                    gridSize: '',
                    quiltImageUrl: '',
                    quiltGrid: [],
                    generated: false,
                    aiGeneratedLayouts: [],
                    tile_list: [],
                    total_tiles_uploaded: 0,
                    // Set null values for all dimension fields to ensure complete reset
                    quiltDimensions: {
                        quiltWidth: null,
                        quiltHeight: null,
                        tileWidth: null,
                        tileHeight: null,
                        columns: 0,
                        rows: 0,
                        totalTiles: 0,
                        tileWidthPixels: 0,
                        tileHeightPixels: 0
                    },
                    layoutSuggestions: [] // Explicitly clear layout suggestions in Firestore too
                });
                
                console.log('All files and data cleared successfully');
            } catch (error) {
                console.error('Error clearing files:', error);
            }
        }
        
        // Reset all state variables
        setUploadedImages([]);
        setGridSize('');
        setGenerated(false);
        setRequiredImages(0); // Explicitly set to 0, not default value
        setShowWarning('');
        setQuiltImageUrl('');
        setPreviousLayoutType('');
        setGenerateDisabled(false);
        setIsEditUnlocked(false);
        setQuiltGrid([]);
        setFeedbackSubmitted(false);
        setRating(0);
        
        // Reset AI generated layouts if the function exists
        if (setAIGeneratedLayouts) {
            setAIGeneratedLayouts([]);
        }
        
        // Reset quilt dimensions with explicit null values if the function exists
        if (setQuiltDimensions) {
            setQuiltDimensions({
                quiltWidth: null,
                quiltHeight: null,
                tileWidth: null,
                tileHeight: null,
                columns: 0,
                rows: 0,
                totalTiles: 0,
                tileWidthPixels: 0,
                tileHeightPixels: 0
            });
        }
        
        // Clear layout suggestions if the function exists
        if (setLayoutSuggestions) {
            setLayoutSuggestions([]);
        }
        
        // Force all input fields to reset
        setTimeout(() => {
            const inputFields = document.querySelectorAll('input[type="text"], input[type="number"]');
            inputFields.forEach(input => {
                input.value = '';
                
                // Dispatch input and change events to ensure React controlled components update
                const inputEvent = new Event('input', { bubbles: true });
                const changeEvent = new Event('change', { bubbles: true });
                input.dispatchEvent(inputEvent);
                input.dispatchEvent(changeEvent);
            });
            
            // Also reset any select dropdowns
            const selectFields = document.querySelectorAll('select');
            selectFields.forEach(select => {
                select.selectedIndex = 0;
                const changeEvent = new Event('change', { bubbles: true });
                select.dispatchEvent(changeEvent);
            });
            
            // Update state one more time after input fields are reset
            setRequiredImages(0);
            setGridSize('');
            if (setQuiltDimensions) {
                setQuiltDimensions({
                    quiltWidth: null,
                    quiltHeight: null,
                    tileWidth: null,
                    tileHeight: null,
                    columns: 0,
                    rows: 0,
                    totalTiles: 0,
                    tileWidthPixels: 0,
                    tileHeightPixels: 0
                });
            }
        }, 100);
        
        // IMPORTANT: Always set current step to 1 when doing a full reset
        setCurrentStep(1);
        
    } catch (error) {
        console.error('Error during reset:', error);
    } finally {
        setIsResetLoading(false);
        closeResetModal();
    }
};

// Function to delete all documents in a specific subcollection
const deleteSubcollection = async (subcollectionPath) => {
    try {
        const subcollectionRef = collection(db, subcollectionPath); // Reference the subcollection
        const querySnapshot = await getDocs(subcollectionRef); // Get all documents in the subcollection
        const deletePromises = querySnapshot.docs.map((doc) => deleteDoc(doc.ref)); // Delete each document
        await Promise.all(deletePromises); // Wait for all deletes to complete
        console.log(`Deleted all documents in subcollection: ${subcollectionPath}`);
    } catch (error) {
        console.error(`Error deleting subcollection ${subcollectionPath}:`, error);
    }
};

export const softReset = (
    setGenerated,
    setQuiltImageUrl,
    setPreviousLayoutType,
    setGenerateDisabled,
    setIsEditUnlocked,
    setQuiltGrid,
    setFeedbackSubmitted,
    setRating,
    setAIGeneratedLayouts,
    closeResetModal
) => {
    try {
        // Reset only the quilt-related states, keeping uploaded images
        setGenerated(false);
        setQuiltImageUrl("");
        setPreviousLayoutType("");
        setGenerateDisabled(false);
        setIsEditUnlocked(false);
        setQuiltGrid([]);
        setFeedbackSubmitted(false);
        setRating(0);
        setAIGeneratedLayouts([]);

        // Update session storage to reflect the soft reset
        const savedState = JSON.parse(sessionStorage.getItem("quiltState") || "{}");
        // Make sure uploadedImages are preserved but marked as processed
        const preservedImages = savedState.uploadedImages ? 
            savedState.uploadedImages.map(img => ({
                ...img, 
                processed: true,
                // Add timestamp to ensure React recognizes a change
                resetTimestamp: Date.now()
            })) : 
            [];
            
        const newState = {
            ...savedState,
            quiltImageUrl: "",
            quiltGrid: [],
            generated: false,
            aiGeneratedLayouts: [],
            uploadedImages: preservedImages
        };
        sessionStorage.setItem("quiltState", JSON.stringify(newState));
        
        console.log("Soft reset completed. Keeping uploaded images, clearing layout.");
    } catch (error) {
        console.error("Error during soft reset:", error);
    }
    
    if (closeResetModal) {
        closeResetModal();
    }
};

